const FROM_V2_KEY = 'fromV2';
const SOURCE_PARAMETER_KEY = 'source-parameter';

// V2 sends source=v2-platform. Keep that literal so a missing env var cannot
// turn the Shop handoff into a redirect loop. platform-v2 is the older marker.
export function isV2PlatformSource(source) {
  return (
    source === process.env.VUE_APP_V2_PLATFORM_SOURCE ||
    source === 'v2-platform' ||
    source === 'platform-v2'
  );
}

function windowHref() {
  if (typeof window === 'undefined' || !window.location) return null;
  return window.location.href || null;
}

function windowPath() {
  if (typeof window === 'undefined' || !window.location) return '';
  return window.location.pathname || '';
}

export function getSourceFromLocation(locationHref) {
  try {
    if (!locationHref) return null;
    const url = locationHref.charAt(0) === '/'
      ? new URL(locationHref, 'https://local.invalid')
      : new URL(locationHref);
    return url.searchParams.get('source');
  } catch (e) {
    return null;
  }
}

export function sanitizeRedirectPath(redirect) {
  const fallback = '/org-owner/orders';
  if (!redirect) return fallback;
  if (!redirect.startsWith('/') || redirect.startsWith('//')) return fallback;
  return redirect;
}

// Orders stay in V1 too: V2 has no Store order history.
const STORE_OR_ORDERS_SEGMENTS = new Set(['shop', 'orders', 'checkout', 'product', 'order-complete']);

// Store share links (/p/:id, /b/:id, /c/:id, /s/:slug) route to the real page after the guard runs.
const STORE_SHARE_SEGMENTS = new Set(['p', 'b', 'c', 's']);

// Match a path segment, not a substring, so /workshop and /orders-approval-settings stay out.
export function isStoreOrOrdersPath(path) {
  if (!path) return false;
  const segments = String(path).split('?')[0].split('#')[0].split('/');
  if (STORE_SHARE_SEGMENTS.has(segments[1]) && segments[2]) return true;
  for (let index = 1; index < segments.length; index += 1) {
    if (STORE_OR_ORDERS_SEGMENTS.has(segments[index])) return true;
  }
  return false;
}

export function markFromV2Platform() {
  sessionStorage.setItem(FROM_V2_KEY, 'true');
  localStorage.removeItem(FROM_V2_KEY);
}

export function clearFromV2Platform() {
  sessionStorage.removeItem(FROM_V2_KEY);
  sessionStorage.removeItem(SOURCE_PARAMETER_KEY);
  localStorage.removeItem(FROM_V2_KEY);
  if (isV2PlatformSource(localStorage.getItem(SOURCE_PARAMETER_KEY))) {
    localStorage.removeItem(SOURCE_PARAMETER_KEY);
  }
}

export function isFromV2Session() {
  return sessionStorage.getItem(FROM_V2_KEY) === 'true'
    || isV2PlatformSource(sessionStorage.getItem(SOURCE_PARAMETER_KEY));
}

// V2's marker stays in this tab. Other sources stay in localStorage, which iframes already use.
export function storePlatformSource(source) {
  if (!source) return;
  if (isV2PlatformSource(source)) {
    sessionStorage.setItem(SOURCE_PARAMETER_KEY, source);
    return;
  }
  localStorage.setItem(SOURCE_PARAMETER_KEY, source);
}

// The Store only exists in V1, so a Store/Orders destination never redirects,
// even in a new tab. Otherwise the handoff is only the tab that just arrived
// from V2, while the login URL still carries the source marker.
export function isV2ShopHandoffActive(path, locationHref) {
  if (isStoreOrOrdersPath(path)) return true;
  if (sessionStorage.getItem(FROM_V2_KEY) !== 'true') return false;
  return isV2PlatformSource(getSourceFromLocation(locationHref));
}

// Shop SSO from V2 lands on /login?source=v2-platform before LoginJWT can run.
// Remember it for this tab only, so a later visit to the old site still redirects.
// No argument reads the current page. Pass { href } to read that URL only,
// including an empty href, so a router guard never falls back to the page being left.
export function captureV2PlatformSource(options) {
  const href = options ? options.href : windowHref();
  const source = getSourceFromLocation(href);
  if (!isV2PlatformSource(source)) return false;
  markFromV2Platform();
  storePlatformSource(source);
  return true;
}

// V1 pages opened from emailed order-approval links. V2 has no equivalent, so
// they never redirect: the S4 approval page, and the inventory request link
// that hands the requester on to Checkout. Keep in sync with routeGuards ROUTES.
const MIGRATION_EXEMPT_PATHS = new Set(['s4-approval-response']);
const MIGRATION_EXEMPT_ID_PATHS = new Set(['inventory-request']);

// Match whole segments, case-insensitively like vue-router, so lookalikes stay out.
export function isMigrationExemptPath(path) {
  if (!path) return false;
  const segments = String(path).split('?')[0].split('#')[0].toLowerCase().replace(/\/+$/, '').split('/');
  if (segments.length === 2) return MIGRATION_EXEMPT_PATHS.has(segments[1]);
  if (segments.length === 3) return MIGRATION_EXEMPT_ID_PATHS.has(segments[1]) && Boolean(segments[2]);
  return false;
}

// The path the redirect is decided on: the destination route, or the current
// page before navigation. Path only: the query can carry a login secret.
export function migrationRoutePath(route) {
  return route == null ? windowPath() : (route.path || '');
}

// Why the redirect is skipped for this route: 'excluded_route' for exempt
// links and Store/Orders, 'fromV2' for the tab that just arrived from the V2
// shop, or null when it is not bypassed. Writes storage, like the boolean below.
export function migrationRedirectBypassReason(route) {
  const useWindow = route == null;
  const href = useWindow ? windowHref() : (route.href || '');
  const routePath = migrationRoutePath(route);
  if (isMigrationExemptPath(routePath)) return 'excluded_route';
  if (useWindow) captureV2PlatformSource();
  else captureV2PlatformSource({ href: href });
  if (isStoreOrOrdersPath(routePath)) return 'excluded_route';
  if (isV2ShopHandoffActive(routePath, href)) return 'fromV2';
  clearFromV2Platform();
  return null;
}

// Writes storage. Pass { path, href } for the destination route. Omit it only
// before navigation, when the current page is the one being decided.
export function resolveMigrationRedirectBypass(route) {
  return migrationRedirectBypassReason(route) !== null;
}
